import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, IconButton, Typography } from "@mui/material";
import React, { useState } from "react";
import swal from "sweetalert";
import ConfirmationModal from "../../components/ConfirmationModal";
import CustomTable from "../../components/CustomTable";
import MainLayout from "../../components/MainLayout";
import axiosInstance from "../../utils/axiosInstance";
import { checkPermission } from "../../utils/checkPermission";
import { formatCustomDate } from "../../utils/formatters";
import ServiceTypeModal from "./modal";

export default function ServiceCenterTypes() {
  const [refresh, setRefresh] = useState(false);
  const [selectedType, setSelectedType] = useState();
  const [openEdit, setOpenEdit] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);

  const columns = [
    { field: "id", headerName: "ID", width: 70, type: "number" },
    { field: "name", headerName: "Name", width: 500 },
    {
      field: "created_at",
      headerName: "Created At",
      width: 500,
      valueGetter: (value) => value && new Date(value),
      renderCell: (value) => formatCustomDate(value.row.created_at),
      type: "date",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      type: "actions",
      renderCell: (value) => (
        <Box>
          {checkPermission("service-type-update") && (
            <IconButton
              color="info"
              onClick={() => {
                setSelectedType(value.row);
                setOpenEdit(true);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
          {checkPermission("service-type-update") && (
            <IconButton
              color="error"
              onClick={() => {
                setSelectedType(value.row);
                setOpenConfirm(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const handleModalClose = () => {
    setOpenAdd(false);
    setOpenEdit(false);
    setOpenConfirm(false);
    setSelectedType(null);
  };

  const handleAddType = async (type) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/service_types`,
        type,
      );

      if (response.status === 201) {
        handleModalClose();
        setRefresh(!refresh);
        swal("Successfully added a new service center type", {
          icon: "success",
        });
      }
    } catch (error) {
      swal("Failed to add service center type", { icon: "error" });
    }
  };

  const handleEditType = async (type) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_URL}/service_types/${selectedType.id}`,
        type,
      );

      if (response.status === 200) {
        handleModalClose();
        setRefresh(!refresh);
        swal("Successfully updated the service center type", {
          icon: "success",
        });
      }
    } catch (error) {
      swal("Failed to update service center type", { icon: "error" });
    }
  };

  const handleDeleteType = async () => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/service_types/${selectedType.id}`,
      );

      if (response.status === 200) {
        handleModalClose();
        setRefresh(!refresh);
        swal("Successfully deleted the service center type", {
          icon: "success",
        });
      }
    } catch (error) {
      swal("Failed to delete service center type", { icon: "error" });
    }
  };

  return (
    <>
      <MainLayout>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Service Center Types</Typography>
          {checkPermission("service-type-create") && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAdd(true)}
            >
              Add new
            </Button>
          )}
        </Box>

        <CustomTable
          columns={columns}
          fetchDataUrl="service_types_paginated"
          refresh={refresh}
        />

        {openAdd && (
          <ServiceTypeModal
            open={openAdd}
            onClose={handleModalClose}
            onSubmit={handleAddType}
          />
        )}

        {openEdit && selectedType && (
          <ServiceTypeModal
            open={openEdit}
            onClose={handleModalClose}
            onSubmit={handleEditType}
            data={selectedType}
          />
        )}
      </MainLayout>

      {openConfirm && (
        <ConfirmationModal
          open={openConfirm}
          onClose={handleModalClose}
          onConfirm={handleDeleteType}
          title="Are you sure you want to delete this item?"
          message={
            <span>
              <strong>{selectedType?.name}</strong> will be deleted. Please note
              that this action is not reversible.
            </span>
          }
        />
      )}
    </>
  );
}
